import { Component, Input, ViewChild } from "@angular/core";
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from "@angular/forms";
import {
    CONDITIONAL_REPORT_TABS,
    EXCESS_VALUE_LIMIT,
    META_INTERNAL_FIELDS,
    REPORT_TABS,
    REPORT_TYPES,
} from "./reports.constants";
import { Dataset } from "src/app/model/dataset/dataset";
import { GroupService } from "src/app/data-access-layer/groups/group.service";
import { Group } from "src/app/model/group/group";
import { GroupTreeNode } from "src/app/model/overlay/group/group-with-overlays-tree-node";
import { DatasetFieldScope } from "src/app/model/dataset/dataset-field-scope";
import { DatasetField } from "src/app/model/dataset/field/dataset-field";
import { WorkspaceItemType } from "src/app/model/workspace/workspace-item-type";
import { WorkspaceItem } from "src/app/model/workspace/workspace-item";
import { WorkspaceItemDialogComponent } from "../../workspace-item/projection/workspace-item-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { NotifService } from "src/app/core/notification/notif.service";
import { ReportService } from "src/app/shared/services/renewal-report-service";
import { DatasetFieldType } from "src/app/model/dataset/dataset-field-type";
import { sortArrayByName } from "src/app/core/utils/util-master";
import { AnalyticsUtils } from "src/app/core/analytics/analytics-utils";
import { TreeNode, TreeStructure } from "src/app/model/menu/tree-structure";
import { TreeStructureUtils } from "src/app/core/utils/tree-structure-utils";
import { MatMenuTrigger } from "@angular/material/menu";
import { ObjectUtils } from "src/app/core/utils/object-utils";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { TreeNodeUtils } from "src/app/core/utils/tree-node-utils";

export interface ExcessValue {
    value: number;
    unit: 'M' | 'B';
}
@Component({
    selector: "map-reports",
    templateUrl: "./reports.component.html",
    styleUrls: ["./reports.component.scss"],
})
export class ReportsComponent {
    @ViewChild('portfolioMenuTrigger') portfolioMenuTrigger: MatMenuTrigger;
    @ViewChild('compareWithMenuTrigger') compareWithMenuTrigger: MatMenuTrigger;

    @ViewChild("workspaceItemDialog", { static: true })
    workspaceIemDialog: WorkspaceItemDialogComponent;

    @Input() dataset: Dataset;
    @Input() isCreateMode: boolean;
    @Input() selectedReport: WorkspaceItem;
    @Input() tabsAccess: {
        FLOOD_FEMA: boolean;
    };

    readonly excessValuesLimit = EXCESS_VALUE_LIMIT;
    readonly millionMaxValue = 999;
    readonly billionMaxValue = 999.9;
    readonly constantReportTabs = REPORT_TABS;

    /**
     * Check if all existing excess value inputs have valid values
     */
    canAddExcessValue(): boolean {
        for (let i = 0; i < this.excessValues.length; i++) {
            const control = this.excessValues.at(i);
            const valueControl = control.get('value');
            const value = valueControl?.value;
            
            // If any input is empty or invalid, cannot add new
            if (value === null || value === '' || value === undefined || valueControl?.invalid) {
                return false;
            }
        }
        return true;
    }

    /**
     * Check if a specific excess value input is valid
     */
    canAddFromIndex(index: number): boolean {
        const control = this.excessValues.at(index);
        const valueControl = control.get('value');
        const value = valueControl?.value;
        
        // Check if this specific input is valid
        if (value === null || value === '' || value === undefined || valueControl?.invalid) {
            return false;
        }
        return true;
    }

    /**
     * Validate all excess value inputs and mark them as touched to show errors
     */
    validateExcessValues(): void {
        for (let i = 0; i < this.excessValues.length; i++) {
            const control = this.excessValues.at(i);
            const valueControl = control.get('value');
            
            // Mark as touched to trigger error display
            valueControl?.markAsTouched();
            valueControl?.updateValueAndValidity();
        }
    }

    /**
     * Check if there is more than one entry with the same unit in the entire array
     */
    hasMultipleEntriesOfSameUnit(index: number): boolean {
        const currentUnit = this.excessValues.at(index).get('unit')?.value;
        let count = 0;
        
        for (let i = 0; i < this.excessValues.length; i++) {
            if (this.excessValues.at(i).get('unit')?.value === currentUnit) {
                count++;
            }
        }
        
        return count > 1;
    }

    /**
     * Get the last index of entries with the given unit
     */
    getLastIndexOfUnit(unit: 'M' | 'B'): number {
        let lastIndex = -1;
        
        for (let i = 0; i < this.excessValues.length; i++) {
            if (this.excessValues.at(i).get('unit')?.value === unit) {
                lastIndex = i;
            }
        }
        
        return lastIndex;
    }
    readonly reportTypes = Object.values(REPORT_TYPES);
    readonly constantReportTabKeys = Object.keys(REPORT_TABS);

    selectedReportTabs: string[] = [];
    reportForm: FormGroup;
    eligibleGroups: Group[];
    internalFields: DatasetField[];
    isSubmitted: boolean = false;
    renewalReports: WorkspaceItem[];
    activeReport: WorkspaceItem;

    groupsStrcuture: TreeNode[];

    portfolioGroupTreeStructure = new TreeNodeUtils();
    portfolioGroupTreeControl = this.portfolioGroupTreeStructure.getTreeControl();
    portfolioGroupDataSource = this.portfolioGroupTreeStructure.getDataSource();
    selectedPortfolioGroup: any;

    compareWithGroupsStrcuture: TreeNode[];
    compareWithGroupTreeStructure = new TreeNodeUtils();
    compareWithGroupTreeControl = this.compareWithGroupTreeStructure.getTreeControl();
    compareWithGroupDataSource = this.compareWithGroupTreeStructure.getDataSource();
    selectedCompareWithGroup: any;

    constructor(
        private formBuilder: FormBuilder,
        private readonly groupService: GroupService,
        public readonly dialog: MatDialog,
        public readonly notifService: NotifService,
        private readonly reportService: ReportService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.getGroups();
        this.filterInternalFields();
        this.activeReport = this.selectedReport;
    }

    filterInternalFields() {
        this.internalFields = sortArrayByName(
            this.dataset?.fields?.filter(
                (field) =>
                    field.scope === DatasetFieldScope.INTERNAL &&
                    field.baseType === DatasetFieldType.NUMBER &&
                    !META_INTERNAL_FIELDS.includes(field.id)
            )
        );
    }

    getGroups() {
        // this.groupService
        //     .getGroupsAsTree(this.dataset.id, this.dataset.accountID)
        //     .subscribe((groups) => {
        //         this.eligibleGroups = this.findLastChildNodes(groups);

        //         if (!this.isCreateMode) {
        //             this.prepopulateForm();
        //         }
        //     });

        this.groupService
            .getGroups(this.dataset.id, this.dataset.accountID)
            .subscribe((response) => {
                const data = JSON.parse(JSON.stringify(response));
                this.groupsStrcuture =
                    AnalyticsUtils.transformTreeStructure(data);
                this.compareWithGroupsStrcuture = ObjectUtils.clone(this.groupsStrcuture);    
                this.portfolioGroupDataSource.data = this.groupsStrcuture;
                this.compareWithGroupDataSource.data = this.compareWithGroupsStrcuture;
                if (!this.isCreateMode) {
                    this.prepopulateForm();
                }
            });
    }

    findLastChildNodes(nodes: GroupTreeNode[]): Group[] {
        let eligibleGroups: Group[] = [];

        for (const node of nodes) {
            if (!node.children.length) {
                eligibleGroups.push(node.value);
            }
            if (node.children.length > 0) {
                eligibleGroups = eligibleGroups.concat(
                    this.findLastChildNodes(node.children)
                );
            }
        }

        return eligibleGroups;
    }

    setActiveReport(report) {
        this.activeReport = { ...report, isDeletable: true };
        this.isCreateMode = false;
        this.reportService.fetchReports(
            this.dataset.id,
            this.dataset.accountID
        );
    }

    initForm() {
        this.reportForm = this.formBuilder.group({
            reportType: [this.reportTypes[0], Validators.required],
            portfolio: ["", Validators.required],
            compareWith: [""],
            referenceValue: ["", Validators.required],
            excessValues: this.formBuilder.array([]),
        });

        if (this.isCreateMode) {
            // Add default Million and Billion inputs directly (without validation)
            this.excessValues.push(
                this.formBuilder.group({
                    value: ['', [this.millionValidator.bind(this)]],
                    unit: ['M']
                })
            );
            this.excessValues.push(
                this.formBuilder.group({
                    value: ['', [this.billionValidator.bind(this)]],
                    unit: ['B']
                })
            );
        }
    }

    prepopulateForm() {
        const parsedData = JSON.parse(this.activeReport.data.trim());
        if (parsedData) {
            this.reportForm.patchValue({
                reportType: this.activeReport.type,
                portfolio: parsedData.portfolio,
                compareWith: parsedData.compareWith,
                referenceValue: parsedData.referenceValue,
            });
            this.groupCheckMarkEvent(this.portfolioGroupDataSource.data, parsedData.portfolio, 'portfolio', parsedData.compareWith);
            this.groupCheckMarkEvent(this.compareWithGroupDataSource.data, parsedData.compareWith, 'compare', parsedData.portfolio);
            this.selectedReportTabs = parsedData.renewalReportTabs || [];

            if (!parsedData.defineExcessValues?.length) {
                // Add default Million and Billion inputs
                this.addExcessValue('M');
                this.addExcessValue('B');
            } else {
                parsedData.defineExcessValues.forEach((excessValue: ExcessValue | number) => {
                    this.populateExcessValue(excessValue);
                });

                const hasMillion = this.excessValues.controls.some(c => c.get('unit')?.value === 'M');
                const hasBillion = this.excessValues.controls.some(c => c.get('unit')?.value === 'B');

                if (!hasMillion) {
                    this.excessValues.insert(0, this.formBuilder.group({
                        value: ['', [this.millionValidator.bind(this)]],
                        unit: ['M']
                    }));
                }
                if (!hasBillion) {
                    this.excessValues.push(this.formBuilder.group({
                        value: ['', [this.billionValidator.bind(this)]],
                        unit: ['B']
                    }));
                }
            }
        }
    }

    get excessValues() {
        return this.reportForm.get("excessValues") as FormArray;
    }

    /**
     * Custom validator for Million values (max 999, integer only)
     */
    millionValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value;
        if (value === null || value === '' || value === undefined) {
            return null;
        }
        
        const numValue = Number(value);
        
        // Check if it's an integer
        if (!Number.isInteger(numValue)) {
            return { decimal: true };
        }
        
        // Check max value
        if (numValue > 999) {
            return { maxMillion: true };
        }
        
        // Check min value
        if (numValue < 1) {
            return { min: true };
        }
        
        return null;
    }

    /**
     * Custom validator for Billion values (max 999.9, up to 1 decimal)
     */
    billionValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value;
        if (value === null || value === '' || value === undefined) {
            return null;
        }
        
        const numValue = Number(value);
        
        // Check if value is a valid number
        if (isNaN(numValue)) {
            return { invalidNumber: true };
        }
        
        // Check decimal places (max 1)
        const decimalPart = value.toString().split('.')[1];
        if (decimalPart && decimalPart.length > 1) {
            return { maxDecimal: true };
        }
        
        // Check max value
        if (numValue > 999.9) {
            return { maxBillion: true };
        }
        
        // Check min value (allow 0.1 and above)
        if (numValue < 0.1) {
            return { min: true };
        }
        
        return null;
    }

    populateExcessValue(excessValue: ExcessValue | number) {
        // Handle legacy format (plain number defaults to Million)
        let value: number;
        let unit: 'M' | 'B';
        
        if (typeof excessValue === 'number') {
            value = excessValue;
            unit = 'M';
        } else {
            value = excessValue.value;
            unit = excessValue.unit;
        }
        
        const validators = unit === 'M' 
            ? [this.millionValidator.bind(this)]
            : [this.billionValidator.bind(this)];
        
        this.excessValues.push(
            this.formBuilder.group({
                value: [value, validators],
                unit: [unit]
            })
        );
    }

    addExcessValue(unit?: 'M' | 'B', clickedIndex?: number) {
        // If unit not specified, use the same unit as the clicked row
        if (!unit) {
            const referenceIndex = clickedIndex !== undefined ? clickedIndex : this.excessValues.length - 1;
            if (referenceIndex >= 0) {
                const referenceUnit = this.excessValues.at(referenceIndex).get('unit')?.value;
                unit = referenceUnit; // Same unit as clicked row (not alternating)
            } else {
                unit = 'M'; // Default to Million if no entries
            }
        }
        
        // Validate only the clicked input
        if (clickedIndex !== undefined) {
            const clickedControl = this.excessValues.at(clickedIndex);
            const valueControl = clickedControl.get('value');
            
            // Mark as touched to show errors
            valueControl?.markAsTouched();
            valueControl?.updateValueAndValidity();
            
            // Check if the clicked input is valid
            const value = valueControl?.value;
            if (value === null || value === '' || value === undefined || valueControl?.invalid) {
                // Don't add new entry if the clicked input is invalid
                return;
            }
        }
        
        const validators = unit === 'M' 
            ? [this.millionValidator.bind(this)]
            : [this.billionValidator.bind(this)];
        
        const newFormGroup = this.formBuilder.group({
            value: ['', validators],
            unit: [unit]
        });
        
        // Always append to end - CSS grid handles positioning
        this.excessValues.push(newFormGroup);
    }

    removeExcessValue(index: number) {
        this.excessValues.removeAt(index);
    }

    onSubmit(isUpdateCall: boolean = true) {
        this.isSubmitted = true;
        if (!this.removeEmptyControlsOfExcessValues()) {
            this.addExcessValue();
        }
        if (this.reportForm.valid && this.selectedReportTabs.length) {
            this.saveReport(isUpdateCall);
        } else {
            this.markFormGroupAndArraysTouched(this.reportForm);
        }
    }

    markFormGroupAndArraysTouched(formGroup: FormGroup | FormArray) {
        Object.values(formGroup.controls).forEach((control) => {
            if (control instanceof FormControl) {
                control.markAsTouched();
            } else if (control instanceof FormGroup) {
                this.markFormGroupAndArraysTouched(control);
            } else if (control instanceof FormArray) {
                control.controls.forEach((arrayControl) => {
                    arrayControl.markAsTouched();
                });
            }
        });
    }

    toggleSelection(key: string) {
        if (this.selectedReportTabs.includes(key)) {
            this.selectedReportTabs = this.selectedReportTabs.filter(
                (item) => item !== key
            );
        } else {
            this.isSubmitted = false;
            this.selectedReportTabs.push(key);
        }
    }

    saveReport(isUpdateCall) {
        const report = this.prepareReport();

        this.workspaceIemDialog.open(
            [report],
            WorkspaceItemType.RENEWAL_REPORT,
            isUpdateCall
        );
    }

    removeEmptyControlsOfExcessValues() {
        const formArray = this.excessValues;
        for (let i = formArray.length - 1; i >= 0; i--) {
            const control = formArray.at(i);
            const valueControl = control.get('value');
            const value = valueControl?.value;
            
            if (
                value === null ||
                value === "" ||
                value === undefined
            ) {
                formArray.removeAt(i);
            }
        }

        return formArray.length;
    }

    prepareReport() {
        const reportFormValues = this.reportForm.value;
        
        // Transform excess values to include unit information
        const excessValues: ExcessValue[] = reportFormValues.excessValues
            .filter((item: any) => item.value !== null && item.value !== '' && item.value !== undefined)
            .map((item: any) => ({
                value: Number(item.value),
                unit: item.unit
            }));
        
        return {
            portfolio: reportFormValues.portfolio,
            compareWith: reportFormValues.compareWith,
            referenceValue: reportFormValues.referenceValue,
            renewalReportTabs: this.selectedReportTabs,
            defineExcessValues: excessValues,
            groups: [],
        };
    }

    deleteRenewalReport(report) {
        this.reportService.deleteRenewalReport(
            report,
            this.dataset.id,
            this.dataset.accountID
        );
    }

    downloadRenewalReport(report) {
        this.reportService.downloadRenewalReport(report);
    }

    closeReportPanel() {
        this.reportService.closeRenewalReportPanel();
    }

    hasTabAccess(key) {
        if (CONDITIONAL_REPORT_TABS.includes(key)) {
            return this.tabsAccess[key];
        }

        return true;
    }

    selectePortfolioGroup(group) {
        this.portfolioMenuTrigger.closeMenu();
        const portfolioControl = this.reportForm.get("portfolio");
        portfolioControl?.setValue(group.id);
        portfolioControl?.markAsTouched();
        this.toggleItem(group, 'portfolio');
    }

    selectCompareWithGroup(group) {
        this.compareWithMenuTrigger.closeMenu();
        const compareWithControl = this.reportForm.get("compareWith");
        compareWithControl?.setValue(group.id);
        compareWithControl?.markAsTouched();
        this.toggleItem(group, 'compare');
    }

    isPorfolioControlInvalid () {
        return this.reportForm.get('portfolio').invalid && this.reportForm.get('portfolio').touched
    }

    isCompareWithControlInvalid () {
        return this.reportForm.get('compareWith').invalid && this.reportForm.get('compareWith').touched
    }

    getNodeClass(node) {
        return node.disabled ? 'disabled-node  mat-tree-node sub-menu-checkbox-options' :'mat-tree-node sub-menu-checkbox-options';
    }

    getParentNodeClass(node) {
        return node.disabled ? 'disabled-node  savedFilterConfiguration-inner-parent' :'savedFilterConfiguration-inner-parent';
    }

    toggleItem(item: TreeNode, section: 'compare' | 'portfolio') {
        if(section == 'portfolio') {
            this.selectedPortfolioGroup = item;
            this.markAllNodeAsEnabled(this.compareWithGroupDataSource.data);
            this.groupCheckEvent(this.compareWithGroupDataSource.data, item.id, 'compare');
        } else if(section == 'compare') {
            this.selectedCompareWithGroup = item;
            this.markAllNodeAsEnabled(this.portfolioGroupDataSource.data);
            this.groupCheckEvent(this.portfolioGroupDataSource.data, item.id, 'portfolio');
        }
    }

    groupCheckEvent(nodes: TreeNode[], id: number, section: string) {
        for (const node of nodes) {
            if (['compare', 'portfolio'].includes(section) && node.id == id) {
                node.disabled = true;
            }
            if (node.children) {
                this.groupCheckEvent(node.children, id, section);
            }
        }
    }

    markAllNodeAsEnabled(nodes: TreeNode[]) {
        for (const node of nodes) {
            if (node.disabled) {
                node.disabled = false;
            } 
            if (node.children) {
                this.markAllNodeAsEnabled(node.children);
            }
        }
    }

    groupCheckMarkEvent(nodes: TreeNode[], markSelectedId: number, section: string, markDisabledId: number) {
        for (const node of nodes) {
            if (section === 'compare' && markSelectedId == node.id) {
                this.selectedCompareWithGroup = node;
            } else if (section === 'portfolio' && markSelectedId == node.id) {
                this.selectedPortfolioGroup = node;
            } else if (['compare', 'portfolio'].includes(section) && markDisabledId == node.id) {
                node.disabled = true;
            }

            if (node.children) {
                this.groupCheckMarkEvent(node.children, markSelectedId, section, markDisabledId);
            }
        }
    }
}
